import { User } from "@/generated/email_parser.openapi";

export type ChallengeType = "status_challenge_2" | "status_challenge_3";

export interface ChallengeThresholds {
  nightsRequiredSilver: number;
  spendingRequiredSilver: number;
  durationDays: number | null; // null = fixed end date
  fixedEndDate: string | null; // null = rolling duration from enrollment
  silverCashback: string;
}

/**
 * Pre-enrollment thresholds per challenge type.
 * Once enrolled, the API returns the actual thresholds via ActiveStatusChallengeResponse.
 */
export const CHALLENGE_THRESHOLDS: Record<
  Extract<ChallengeType, "status_challenge_2">,
  ChallengeThresholds
> = {
  status_challenge_2: {
    nightsRequiredSilver: 3,
    spendingRequiredSilver: 1000,
    durationDays: 7,
    fixedEndDate: null,
    silverCashback: "5%",
  },
};

export interface EligibilityResult {
  eligible: boolean;
  challengeType: ChallengeType | null;
  reason?: string;
}

/**
 * Fallback Gold thresholds. No Gold challenge is configured yet (the live
 * challenge is Silver-only), so these placeholders are only used if the API ever
 * returns a dual-tier challenge without explicit Gold targets. Keep them here so
 * the value lives in exactly one place across the card, banner, and header CTA.
 */
export const GOLD_FALLBACK = { nights: 10, spending: 3000 } as const;

export interface ChallengeTarget {
  /** True once Silver is met on a dual-tier challenge (user now chasing Gold). */
  chasingGold: boolean;
  tier: "Silver" | "Gold";
  cashbackPct: "5%" | "7%";
  nightsTarget: number;
  spendingTarget: number;
  nightsRemaining: number;
}

/**
 * Resolve the tier an active challenge is targeting plus its night/spend targets
 * and remaining nights. Centralizes the `chasingGold` rule, tier copy, and the
 * Gold fallback so the challenge card, rewards banner, and header CTA stay in
 * lockstep. For pre-enrollment surfaces, callers use `CHALLENGE_THRESHOLDS`
 * directly (the API hasn't returned thresholds yet).
 */
export function resolveChallengeTarget(input: {
  isActive: boolean;
  isSilverOnly: boolean;
  statusChallengeComplete: "silver" | "gold" | null;
  nightsCurrent: number;
  nightsRequiredSilver: number | null;
  spendingRequiredSilver: number | null;
  nightsRequiredGold: number | null;
  spendingRequiredGold: number | null;
}): ChallengeTarget {
  const hasSilverThresholds =
    input.nightsRequiredSilver != null && input.spendingRequiredSilver != null;
  const chasingGold =
    input.isActive &&
    !input.isSilverOnly &&
    (input.statusChallengeComplete === "silver" || !hasSilverThresholds);
  const tier = chasingGold ? "Gold" : "Silver";
  const cashbackPct = chasingGold ? "7%" : "5%";
  const nightsTarget = chasingGold
    ? input.nightsRequiredGold ?? GOLD_FALLBACK.nights
    : input.nightsRequiredSilver ??
      CHALLENGE_THRESHOLDS.status_challenge_2.nightsRequiredSilver;
  const spendingTarget = chasingGold
    ? input.spendingRequiredGold ?? GOLD_FALLBACK.spending
    : input.spendingRequiredSilver ??
      CHALLENGE_THRESHOLDS.status_challenge_2.spendingRequiredSilver;
  const nightsRemaining = Math.max(nightsTarget - input.nightsCurrent, 0);
  return {
    chasingGold,
    tier,
    cashbackPct,
    nightsTarget,
    spendingTarget,
    nightsRemaining,
  };
}

/**
 * Determine which challenge type a user is eligible for.
 *
 * Rules:
 * - Users with an ACTIVE challenge can access (to view progress)
 * - Users at Silver+ earning (5%+ cashback) without an active challenge cannot start another
 * - Users who previously attempted but have no active challenge CANNOT access
 * - All other eligible users enroll into status_challenge_2 (Silver-only, 7-day rolling window)
 */
export function getEligibleChallengeType(
  user: Pick<
    User,
    | "activeStatusChallenge"
    | "cashbackPercentage"
    | "hasHistoricalStatusChallenge"
  >
): EligibilityResult {
  if (user.activeStatusChallenge) {
    return { eligible: true, challengeType: null };
  }

  const cashbackPercentage = user.cashbackPercentage ?? 0;
  if (cashbackPercentage >= 0.05) {
    return {
      eligible: false,
      challengeType: null,
      reason:
        "The Status Challenge is for members working toward Silver. Your account already reflects Silver tier or higher benefits.",
    };
  }

  if (user.hasHistoricalStatusChallenge) {
    return {
      eligible: false,
      challengeType: null,
      reason:
        "Status challenges are not available at this time, but we plan to run more in the future.",
    };
  }

  return { eligible: true, challengeType: "status_challenge_2" };
}

/** Boolean helper for hooks and CTAs. */
export function isUserEligibleForStatusChallengeFlow(
  user: Pick<
    User,
    | "activeStatusChallenge"
    | "cashbackPercentage"
    | "hasHistoricalStatusChallenge"
  >
): boolean {
  return getEligibleChallengeType(user).eligible;
}

/**
 * Get the route for the status challenge.
 * Returns null if user is not eligible or user is missing.
 */
export function getStatusChallengeRoute(
  user: User | null | undefined
): string | null {
  if (!user) {
    return null;
  }

  const { eligible } = getEligibleChallengeType(user);

  if (!eligible) {
    return null;
  }

  return "/challenge";
}
